import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, inject } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10nTranslationModule } from 'angular-l10n';
import { PageHeaderService } from '@wonder-core-ng/services/page-header.service';
import { BI_CORE_CONFIG } from '../config';
import { NotificationService } from '../notification-store/notification.service';
import { NotificationMenuComponent } from '../notifications/notification-menu/notification-menu.component';
import { GlobalAssistantComponent } from '../global-assistant/global-assistant.component';
import { CORE_VERSION } from '../core-version';

/**
 * The app's top bar: whatever the routed view publishes through `PageHeaderService` (icon, title,
 * subtitle, inline content and action buttons), plus the global assistant, the notification bell
 * and the avatar.
 *
 * Lives in `wonder-bi-core` because all three BI apps render the same bar; a host only places
 * `<wbi-header-bar>` in its shell. It takes no inputs today — per-project differences, when they
 * appear, belong in `PageHeaderService.set()` (already per-view) or in new inputs, never in a forked
 * copy of this component.
 */
@Component({
  selector: 'wbi-header-bar',
  imports: [
    NotificationMenuComponent,
    GlobalAssistantComponent,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    MatMenuModule,
    MatTooltipModule,
    CommonModule,
    MatDividerModule,
    L10nTranslationModule,
  ],
  templateUrl: './header-bar.component.html',
  styleUrl: './header-bar.component.scss',
})
export class HeaderBarComponent implements OnDestroy {
  private readonly notificationService = inject(NotificationService);
  /** Title + actions published by the current routed view (empty on most routes). */
  readonly pageHeader = inject(PageHeaderService);
  // Optional: the bar predates this token and must keep rendering for a host that never provides
  // it, logo or no logo.
  private readonly biCoreConfig = inject(BI_CORE_CONFIG, { optional: true });

  /** Client logo for this deployment, or null when the host application configures none. */
  readonly brandLogo = this.biCoreConfig?.brandLogo ?? null;

  /** Core release this bundle was built from (WB-298): a quiet mark for the team, no tooltip. */
  readonly coreVersion = CORE_VERSION;

  readonly unreadCount = this.notificationService.unreadCount;
  readonly hasNotifications = computed(() => this.unreadCount() > 0);

  /** Material's badge renders whatever it is given, so the cap is applied here, not in CSS. */
  readonly badgeText = computed(() => {
    const count = this.unreadCount();
    return count > 99 ? '99+' : String(count);
  });

  constructor() {
    // The bar is mounted for the whole session, which makes it the right place to drive the poll that
    // keeps the badge current without a page reload.
    this.notificationService.startPolling();
  }

  ngOnDestroy(): void {
    this.notificationService.stopPolling();
  }
}
